import React, { useState } from 'react';
import {
  Mail,
  MessageSquare,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShieldCheck,
  PhoneCall,
} from 'lucide-react';
import { TELEGRAM_CHAT_URL, TELEGRAM_BOT_USERNAME } from '../../config/telegram';
import { TelegramIcon } from '../../components/TelegramAssistantSection';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'customer_support',
    subject: '',
    message: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please provide your name, email address, and a message.');
      return;
    }

    setSubmitting(true);
    // Simulate safe local submission handling with clear confirmation
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        category: 'customer_support',
        subject: '',
        message: '',
      });
    }, 600);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Header */}
      <div className="max-w-4xl mx-auto text-center space-y-4 pt-4 sm:pt-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200">
          <Mail className="w-4 h-4 text-emerald-600" />
          <span>Support & Inquiries</span>
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
          Contact JobMatch NG
        </h1>
        <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
          Need help with a job request, verification status, or service feedback?
          Reach our team via WhatsApp, direct email, or submit the inquiry form below.
        </p>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
        {/* Contact Info Cards (Left Column) */}
        <div className="space-y-4 md:col-span-1">
          {/* Telegram AI Assistant Card */}
          <div className="bg-gradient-to-br from-slate-900 via-sky-950 to-slate-900 text-white rounded-3xl p-6 border border-sky-800 shadow-md space-y-4 relative overflow-hidden">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center text-sky-400">
                <TelegramIcon className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-sky-300 uppercase tracking-wider block">
                  Official Telegram Assistant
                </span>
                <span className="text-sm font-extrabold text-white font-mono">@{TELEGRAM_BOT_USERNAME}</span>
              </div>
            </div>

            <p className="text-xs text-sky-100/90 leading-relaxed">
              JobMatch NG AI assistant is live on Telegram. Send photos, describe your job, and connect with verified professionals in your LGA.
            </p>

            <a
              href={TELEGRAM_CHAT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
            >
              <TelegramIcon className="w-4 h-4 fill-slate-950" />
              <span>Start Telegram Chat</span>
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>
          </div>

          {/* Email Support Card */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Support Email
                </span>
                <a
                  href="mailto:support@jobmatch.ng"
                  className="text-sm font-extrabold text-slate-900 hover:text-emerald-700 transition-colors"
                >
                  support@jobmatch.ng
                </a>
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              For account support, verification status, and formal inquiries. We typically reply within 24 hours.
            </p>
          </div>

          {/* Location & Operating Hours */}
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Location
                </span>
                <span className="text-sm font-extrabold text-slate-900">Lagos, Nigeria</span>
              </div>
            </div>
            <div className="text-xs text-slate-500 space-y-1">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span>Monday – Saturday: 8:00 AM – 6:00 PM WAT</span>
              </div>
              <p className="text-[11px] text-slate-400 pt-1">
                Emergency WhatsApp tickets are monitored outside standard hours.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form (Right Column) */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-6 md:col-span-2">
          <div className="border-b border-slate-100 pb-4">
            <h2 className="text-xl font-extrabold text-slate-900">Send an Inquiry</h2>
            <p className="text-xs text-slate-500 mt-1">
              Have a question about a quote, verification check, or customer assistance? Send a message below.
            </p>
          </div>

          {submitted && (
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-start gap-3 text-xs leading-relaxed animate-in fade-in">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <strong className="font-bold text-emerald-950">Thank you for reaching out!</strong>
                <p>
                  Your message has been received by our support desk. A representative will get back to
                  you at your provided email address shortly.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 flex items-center gap-2 text-xs">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Babatunde Adeleke"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs sm:text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[42px]"
                />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Email Address *</label>
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs sm:text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[42px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Phone */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Phone Number (Optional)</label>
                <input
                  type="tel"
                  placeholder="+234 ..."
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs sm:text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[42px]"
                />
              </div>

              {/* Category */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Topic Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs sm:text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[42px]"
                >
                  <option value="customer_support">Customer Assistance</option>
                  <option value="artisan_onboarding">Professional Registration & Verification</option>
                  <option value="billing_paystack">Paystack Billing Inquiry</option>
                  <option value="trust_dispute">Trust & Safety Escalation</option>
                  <option value="general">General Feedback</option>
                </select>
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Subject</label>
              <input
                type="text"
                placeholder="Brief summary of your question"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs sm:text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[42px]"
              />
            </div>

            {/* Message */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Message *</label>
              <textarea
                required
                rows={4}
                placeholder="Please describe your question or issue in detail..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs sm:text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs sm:text-sm font-bold shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer min-h-[44px]"
            >
              <Send className="w-4 h-4" />
              <span>{submitting ? 'Sending Message...' : 'Submit Inquiry'}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
